import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Heart, User, MapPin } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { getAddressFromCoordinates, isCoordinateLikeAddress, formatAddressForDisplay } from '../../utils/locationUtils';
import SearchOverlay from '../user/SearchOverlay';

const UserHeader = () => {
  const { userAuth } = useContext(AuthContext);
  const [detectedAddress, setDetectedAddress] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [detectAttempted, setDetectAttempted] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // A stored address that is really a coordinate pair counts as "no address" —
  // older profiles had lat/lng written into location.address when geocoding
  // failed, and rendering it verbatim is the bug this guards against.
  const storedAddress = userAuth?.user?.location?.address;
  const usableStored = isCoordinateLikeAddress(storedAddress) ? null : storedAddress;
  const address = usableStored || detectedAddress;

  // Auto-detect location if not available from auth
  useEffect(() => {
    if (address || detecting || detectAttempted) return;

    // Check localStorage cache first
    try {
      const cached = localStorage.getItem('zammer_header_location');
      if (cached) {
        const parsed = JSON.parse(cached);
        // Older builds cached coordinate strings here — drop those instead of
        // serving them back for the next 30 minutes.
        if (isCoordinateLikeAddress(parsed.address)) {
          localStorage.removeItem('zammer_header_location');
        } else if (Date.now() - parsed.timestamp < 30 * 60 * 1000) {
          setDetectedAddress(parsed.address);
          return;
        }
      }
    } catch {}

    if (!navigator.geolocation) return;

    // One attempt per mount. Without this, a failed lookup leaves address null
    // and detecting false, which re-fires this effect in a loop.
    setDetectAttempted(true);
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          // Backend returns the short, specific form already ("347, Anupam
          // Nagar, Gyan Deep School"), or null — never coordinates.
          const addr = await getAddressFromCoordinates(longitude, latitude);
          const short = formatAddressForDisplay(addr);
          if (short) {
            setDetectedAddress(short);
            localStorage.setItem('zammer_header_location', JSON.stringify({
              address: short,
              timestamp: Date.now()
            }));
          }
          // No address? Show nothing. Coordinates are not a location to a shopper.
        } catch {
          // Silent fail — just don't show location
        }
        setDetecting(false);
      },
      () => setDetecting(false),
      { timeout: 10000, maximumAge: 300000 }
    );
  }, [address, detecting, detectAttempted]);

  const displayAddress = formatAddressForDisplay(address);

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
      <div className="flex items-center justify-between px-4 h-12">
        {/* Left — Brand + Location */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <img src="/images/zammer-official-logo.png" alt="ZAMMER" className="h-5 w-auto object-contain" />
            {displayAddress && (
              <div className="flex items-center gap-0.5 min-w-0">
                <MapPin className="w-3 h-3 text-orange-500 flex-shrink-0" />
                <span className="text-[10px] text-gray-500 truncate max-w-[160px]">
                  {displayAddress}
                </span>
              </div>
            )}
            {detecting && !displayAddress && (
              <span className="text-[10px] text-gray-400 animate-pulse">detecting...</span>
            )}
          </div>
        </div>

        {/* Right — Action Icons */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowSearch(true)}
            className="text-gray-700"
            aria-label="Search"
          >
            <Search className="w-[20px] h-[20px]" strokeWidth={1.8} />
          </button>
          <Link to="/user/wishlist" className="text-gray-700">
            <Heart className="w-[20px] h-[20px]" strokeWidth={1.8} />
          </Link>
          <Link
            to={userAuth.isAuthenticated ? '/user/profile' : '/user/login'}
            className="text-gray-700"
          >
            <User className="w-[20px] h-[20px]" strokeWidth={1.8} />
          </Link>
        </div>
      </div>

      {showSearch && <SearchOverlay onClose={() => setShowSearch(false)} />}
    </header>
  );
};

export default UserHeader;
